import React, { useState, useEffect } from "react";
import { Card, Row, Col, Spinner, Table, Button, Modal, Form } from "@themesberg/react-bootstrap";
import { useSearchParams, useNavigate } from "react-router-dom";
import { fetchApi, postApi } from "../../redux/api";
import { Routes } from "../../routes";
import { formatNumber } from "../../redux/utils";
import BackButton from "../../components/BackButton";
import ResponsiveContainer from "../../components/ResponsiveContainer";

function RetentionView() {
  const [searchParams] = useSearchParams();
  const contractor_id = searchParams.get("contractor_id");
  const contract_no = searchParams.get("contract_no");
  const total_retention = searchParams.get("total_retention");
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [totalReleased, setTotalReleased] = useState(0);
  const [loading, setLoading] = useState(true);

  const retentionBalance = parseFloat(total_retention || 0) - parseFloat(totalReleased || 0);
  const [showRaiseModal, setShowRaiseModal] = useState(false);
  const [amountPaid, setAmountPaid] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState(null);
  const [modalMessage, setModalMessage] = useState(null);

  useEffect(() => {
    setLoading(true);
    fetchApi(`retention-report?contractor_id=${contractor_id}&contract_no=${contract_no}`)
      .then((resp) => {
        if (resp.success && resp.results) {
          setRecords(resp.results);
          setTotalReleased(parseFloat(resp.total_dr || 0));
        }
      })
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  }, [contractor_id, contract_no]);

  const first = records[0] || {};

  const allTaxFields = [
    { label: "VAT (7.5%)", key: "vat" },
    { label: "WHT (2%)", key: "wht_2" },
    { label: "WHT (5%)", key: "wht_5" },
    { label: "SD (1%)", key: "sd" },
    { label: "EL (2%)", key: "el" },
    { label: "CSR", key: "csr" },
    { label: "Vetting fee", key: "vetting_fee" },
    { label: "Retention", key: "retention" },
    { label: "Contract Award fee", key: "contract_award_fee" },
    { label: "Tender", key: "tender" },
    { label: "WR", key: "wr" },
    { label: "Others", key: "others" },
  ];

  const alwaysFields = [
    { label: "Net Amount", key: "net_amount" },
  ];

  const visibleTaxFields = allTaxFields.filter((tf) =>
    records.some((r) => parseFloat(r[tf.key] || 0) !== 0)
  );

  const amountPaidFields = [...visibleTaxFields, ...alwaysFields];

  const totalCr  = records.reduce((a, b) => a + parseFloat(b.cr) , 0)
  const totalDr  = records.reduce((a, b) => a + parseFloat(b.dr) , 0)
  const totalBalance  = totalCr - totalDr

  const handleRaiseRetention = () => {
    setSubmitting(true);
    setModalMessage(null);
    postApi("retention-payment", {
      contract_no,
      contractor_id,
      amount_paid: amountPaid,
      description: `Retention payment - ${contract_no}`,
    })
      .then((resp) => {
        if (resp.success) {
          navigate(Routes.expenditureControl.retentionReport);
        } else {
          setModalMessage({ type: "danger", text: resp.message || "Failed to record payment" });
        }
      })
      .catch((err) => {
        setModalMessage({ type: "danger", text: err.message });
      })
      .finally(() => setSubmitting(false));
  };

  const openRaiseModal = () => {
    setAmountPaid("");
    setModalMessage(null);
    setShowRaiseModal(true);
  };

  return (
    <ResponsiveContainer>
      <BackButton />
      <Card>
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0">Retention Details</h5>
            {!loading && records.length > 0 && retentionBalance > 0 && (
              <Button variant="success" size="sm" onClick={openRaiseModal}>
                Raise Retention
              </Button>
            )}
          </div>

          {loading ? (
            <div className="text-center py-4">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : records.length === 0 ? (
            <p className="text-muted">No retention records found.</p>
          ) : (
            <>
              <p><strong>Contract No.:</strong> {contract_no}</p>
              <p><strong>Contractor Name:</strong> {first.contractor_name || "N/A"}</p>
              {/* <p><strong>Total Contract Amount:</strong> ₦{formatNumber(records.reduce((s, r) => s + parseFloat(r.total_contract_amount || 0), 0))}</p> */}
              <p><strong>Total Retention:</strong> ₦{formatNumber(total_retention)}</p>
              <p><strong>Balance:</strong> ₦{formatNumber(totalBalance)}</p>

              <hr />
              <h5 className="mb-3">Payment Details</h5>

              <div style={{ overflowX: "auto" }}>
              <Table hover bordered responsive size="sm">
                <thead className="table-header">
                  <tr>
                    <th rowSpan={2} style={{ textAlign: "center", verticalAlign: "middle" }}>Date</th>
                    <th rowSpan={2} style={{ textAlign: "center", verticalAlign: "middle" }}>PV Code</th>
                    <th rowSpan={2} style={{ textAlign: "center", verticalAlign: "middle" }}>DR</th>
                    <th rowSpan={2} style={{ textAlign: "center", verticalAlign: "middle" }}>CR</th>
                    {/* <th colSpan={amountPaidFields.length} style={{ textAlign: "center" }}>Amount Paid</th>
                    <th rowSpan={2} style={{ textAlign: "right", verticalAlign: "middle" }}>Gross</th> */}
                  </tr>
                  {/* <tr>
                    {amountPaidFields.map((f) => (
                      <th key={f.key} style={{ textAlign: "right" }}>{f.label}</th>
                    ))}
                  </tr> */}
                </thead>
                <tbody>
                  {records.map((r, i) => (
                    <tr key={i}>
                      <td style={{ textAlign: "center" }}>{r.entry_date}</td>
                      <td style={{ textAlign: "center" }}>
                        <a
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            // navigate(`/pv-pdf/check?approval_no=${r.approval_no}&pv_code=${r.pv_code}`);
                            navigate(`/pv-collection-report/view?approval_no=${r.approval_no}&approval_type=&&pv_code=${r.pv_code}`);
                          }}
                        >
                          {r.pv_code}
                        </a>
                      </td>
                      <td style={{ textAlign: "right" }}>₦{formatNumber(r.dr)}</td>
                      <td style={{ textAlign: "right" }}>₦{formatNumber(r.cr)}</td>

                      {/* {amountPaidFields.map((f) => {
                        const val = f.key === "net_amount"
                          ? r.total_contract_amount - r.total_deduction - total_retention
                          : parseFloat(r[f.key] || 0);
                        return (
                          <td key={f.key} style={{ textAlign: "right" }}>₦{formatNumber(val)}</td>
                        );
                      })} */}
                      {/* <td style={{ textAlign: "right" }}>₦{formatNumber(r.total_contract_amount)}</td> */}
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="fw-bold">
                    <td colSpan={2}></td>
                    <td style={{ textAlign: "right" }}>₦{totalDr?.toLocaleString()}</td>
                    <td style={{ textAlign: "right" }}>₦{totalCr?.toLocaleString()}</td>
                    {/* {amountPaidFields.map((f) => {
                      const val = f.key === "net_amount"
                        ? records.reduce((s, r) => s + (r.total_contract_amount - r.total_deduction - total_retention), 0)
                        : records.reduce((s, r) => s + parseFloat(r[f.key] || 0), 0);
                      return (
                        <td key={f.key} style={{ textAlign: "right" }}>₦{formatNumber(val)}</td>
                      );
                    })} */}
                    {/* <td style={{ textAlign: "right" }}>
                      ₦{formatNumber(records.reduce((s, r) => s + parseFloat(r.total_contract_amount || 0), 0))}
                    </td> */}
                  </tr>
                </tfoot>
              </Table>
              </div>
            </>
          )}

          {message && (
            <div className={`alert alert-${message.type} mt-3`} role="alert">
              {message.text}
            </div>
          )}
        </Card.Body>
      </Card>

      <Modal show={showRaiseModal} onHide={() => setShowRaiseModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Raise Retention Payment</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {modalMessage && (
            <div className={`alert alert-${modalMessage.type}`} role="alert">
              {modalMessage.text}
            </div>
          )}
          <Form>
            <Form.Group className="mb-2">
              <Form.Label>Total Retention Withheld</Form.Label>
              <Form.Control type="text" value={`₦${formatNumber(total_retention || 0)}`} disabled />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Amount Released</Form.Label>
              <Form.Control type="text" value={`₦${formatNumber(totalReleased || 0)}`} disabled />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Outstanding Balance</Form.Label>
              <Form.Control type="text" value={`₦${formatNumber(Math.max(0, retentionBalance))}`} disabled />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Amount Paid</Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                min="0"
                max={Math.max(0, retentionBalance)}
                value={amountPaid}
                onChange={(e) => setAmountPaid(e.target.value)}
                placeholder="Enter amount to release"
              />
              {amountPaid && parseFloat(amountPaid) > Math.max(0, retentionBalance) && (
                <div className="text-danger mt-1" style={{ fontSize: '0.875rem' }}>
                  Amount paid cannot exceed outstanding balance (₦{formatNumber(Math.max(0, retentionBalance))})
                </div>
              )}
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={() => setShowRaiseModal(false)}>
            Cancel
          </Button>
          <Button
            variant="success"
            size="sm"
            onClick={handleRaiseRetention}
            disabled={!amountPaid || parseFloat(amountPaid) <= 0 || parseFloat(amountPaid) > Math.max(0, retentionBalance) || submitting}
          >
            {submitting ? "Processing..." : "Submit"}
          </Button>
        </Modal.Footer>
      </Modal>
    </ResponsiveContainer>
  );
}

export default RetentionView;
